import * as moment from 'moment';
import React, { useState } from 'react';
import ReactGA from 'react-ga4';
import PropTypes from 'prop-types';
import { Box, CircularProgress, Grid, Typography } from '@mui/material';
import { useCreations } from 'contexts/useCreations';
import BaseModal from 'components/BaseModal';
import { UserTypeEnum } from 'enums';
import CreationModal from '../CreationModal';
import CreationCard from '../CreationCard';
import { blackNovemberPromotionData, isAnniversaryActive } from 'utils';
import BlackNovemberLimitModal from '../BlackNovemberLimitModal';
import AnniversaryMindTheGraphModal from '../AnniversaryMindTheGraphModal';
import LimitationModal from '../LimitationModal';
import { useRouter } from 'next/router';
import getCleverTap from 'utils/clevertap';
import { useAuthUser } from 'features/auth/AuthUserContext';

const Props = {
  creations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      deleted: PropTypes.number.isRequired,
      folder: PropTypes.number,
      thumb: PropTypes.string.isRequired,
      slide_id: PropTypes.number.isRequired,
      width: PropTypes.number.isRequired,
      height: PropTypes.number.isRequired,
    }),
  ).isRequired,
  shared: PropTypes.bool,
  addToFolderOption: PropTypes.bool,
  onOptionClick: PropTypes.func.isRequired,
  onModalActionClick: PropTypes.func.isRequired,
  onDeleteModalActionClick: PropTypes.func.isRequired,
};

export default function CreationsList({
  creations,
  shared,
  addToFolderOption,
  onOptionClick,
  onModalActionClick,
  onDeleteModalActionClick,
}) {
  const [allCreations, , loading] = useCreations();
  const { user } = useAuthUser();
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteLimitModalOpen, setDeleteLimitModalOpen] = useState(false);
  const [restoreLimitModalOpen, setRestoreLimitModalOpen] = useState(false);
  const [currentCreation, setCurrentCreation] = useState();
  const promotionBlacknovember = blackNovemberPromotionData();
  const router = useRouter();
  const clevertap = getCleverTap();

  function getOptions(creation) {
    if (shared) {
      return [{ key: 'delete_sharing', label: 'Exit from sharing' }];
    }

    if (creation.deleted) {
      return [{ key: 'restore', label: 'Restore' }];
    }

    if (addToFolderOption) {
      return [
        { key: 'add_to_folder', label: 'Add to folder' },
        { key: 'delete', label: 'Delete' },
      ];
    }

    if (creation.folder) {
      return [
        { key: 'remove_from_folder', label: 'Remove from folder' },
        { key: 'delete', label: 'Delete' },
      ];
    }

    return [{ key: 'delete', label: 'Delete' }];
  }

  function handleOptionClick(option, creation) {
    switch (option.key) {
      case 'restore':
        if (user.tp_user === UserTypeEnum.TRIAL) {
          setRestoreLimitModalOpen(true);
        } else {
          onOptionClick(option, creation);
        }
        break;
      case 'add_to_folder':
        setCurrentCreation(creation);
        setModalOpen(true);
        onOptionClick(option, creation);
        break;
      case 'delete':
        setCurrentCreation(creation);
        setDeleteModalOpen(true);
        onOptionClick(option, creation);
        break;
      case 'remove_from_folder':
        onOptionClick(option, creation);
        break;
      case 'delete_sharing':
        onOptionClick(option, creation);
        break;
    }
  }

  function handleModalActionClick(folderId) {
    onModalActionClick(folderId, currentCreation);

    setCurrentCreation();
    setModalOpen(false);
  }

  function handleModalClose() {
    setCurrentCreation();
    setModalOpen(false);
  }

  async function handleDeleteModalActionClick() {
    ReactGA.event({
      category: 'workspace',
      action: 'delete_creation',
      label: 'Click on "Delete" button in My Creations',
      value: user.id,
    });

    clevertap.event.push('Delete Creation', {
      Product: 'MTG',
      Email: user.email,
      URL: router.asPath,
      Description: 'Delete Creation',
      Locale: 'my-creations',
    });

    if (user.tp_user === UserTypeEnum.TRIAL && user.dt_delete) {
      const lastDeleteDate = moment.utc(user.dt_delete);
      const hoursFromLastDeleteDate = moment.utc().diff(lastDeleteDate, 'hours');

      if (hoursFromLastDeleteDate < 2) {
        setCurrentCreation();
        setDeleteModalOpen(false);

        ReactGA.event({
          category: 'workspace',
          action: 'delete_creation_upgrade_banner',
          label: 'When Banner Presented to free users',
          value: user.id,
        });

        clevertap.event.push('Banner Click', {
          Product: 'MTG',
          Email: user.email,
          'Country code': user.country_code,
          URL: router.asPath,
          Description: 'Delete Creation Upgrade Banner',
          Locale: 'my-creations',
        });

        setDeleteLimitModalOpen(true);

        return;
      }
    }

    await onDeleteModalActionClick(currentCreation);

    setCurrentCreation();
    setDeleteModalOpen(false);
  }

  function handleDeleteModalClose() {
    setCurrentCreation();
    setDeleteModalOpen(false);
  }

  function isBlockedAccess() {
    return false;
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <Box mr={1}>
          <CircularProgress size={20} />
        </Box>
        <Typography variant="body2" color="textSecondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  if (!creations?.length) {
    // TODO: Creations is prop drilling from parent component, but is also fetched here.
    return (
      <Typography variant="body2" color="textSecondary">
        No items
      </Typography>
    );
  }

  return (
    <>
      <Grid container={true} spacing={3}>
        {creations.map((creation) => (
          <Grid key={creation.id} item={true} xs={12} sm={6} md={3}>
            <CreationCard
              creation={creation}
              blockedAccess={isBlockedAccess()}
              options={getOptions(creation)}
              onOptionClick={(option) => handleOptionClick(option, creation)}
            />
          </Grid>
        ))}
      </Grid>

      <CreationModal
        open={modalOpen}
        onActionClick={handleModalActionClick}
        onClose={handleModalClose}
      />

      <BaseModal
        open={deleteModalOpen}
        title={
          <b>
            Delete the creation <i>{currentCreation?.name}</i>?
          </b>
        }
        content={<Typography>Your creation will be moved to the Deleted folder.</Typography>}
        actionText="Delete"
        onActionClick={handleDeleteModalActionClick}
        onClose={handleDeleteModalClose}
      />

      {promotionBlacknovember.isActive && (
        <BlackNovemberLimitModal
          open={deleteLimitModalOpen}
          onClose={() => setDeleteLimitModalOpen(false)}
        />
      )}

      {isAnniversaryActive() && (
        <AnniversaryMindTheGraphModal
          open={deleteLimitModalOpen}
          onClose={() => setDeleteLimitModalOpen(false)}
        />
      )}

      {!promotionBlacknovember.isActive && !isAnniversaryActive() && (
        <LimitationModal
          open={deleteLimitModalOpen}
          onClose={() => setDeleteLimitModalOpen(false)}
        />
      )}

      {promotionBlacknovember.isActive && (
        <BlackNovemberLimitModal
          open={restoreLimitModalOpen}
          onClose={() => setRestoreLimitModalOpen(false)}
        />
      )}

      {isAnniversaryActive() && (
        <AnniversaryMindTheGraphModal
          open={restoreLimitModalOpen}
          onClose={() => setRestoreLimitModalOpen(false)}
        />
      )}

      {!promotionBlacknovember.isActive && !isAnniversaryActive() && (
        <LimitationModal
          open={restoreLimitModalOpen}
          onClose={() => setRestoreLimitModalOpen(false)}
        />
      )}
    </>
  );
}

CreationsList.propTypes = Props;
